import { useId } from 'react';
import { alpha, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TestIds } from '../../../assets/testIds';

interface PlaybookGoalProps {
  goal?: string | null;
}

/**
 * A playbook content's Airtable "Content Goal", shown at the end of the content
 * under a static "It's Working When..." heading.
 */
export default function PlaybookGoal({ goal }: PlaybookGoalProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const text = goal?.trim();

  if (!text) return null;

  return (
    <Box
      component="section"
      data-testid={TestIds.playbookView.goalSection}
      aria-labelledby={titleId}
      sx={(theme) => ({
        p: 2.5,
        borderRadius: '10px',
        // Matches the Figma's #DDE4E8 on SN, and follows each brand's primary
        border: `1px solid ${alpha(theme.palette.brand.primary, 0.2)}`,
        bgcolor: theme.palette.brand.surface,
        display: 'flex',
        flexDirection: 'column',
        gap: '5px',
        minWidth: 0,
        overflowWrap: 'anywhere',
      })}
    >
      <Typography
        component="h2"
        id={titleId}
        sx={(theme) => ({
          fontSize: 20,
          fontWeight: 500,
          lineHeight: 1.6,
          letterSpacing: '0.15px',
          color: theme.palette.brand.primary,
        })}
      >
        {t('content.playbook.goalTitle')}
      </Typography>
      <Typography
        data-testid={TestIds.playbookView.goalText}
        sx={(theme) => ({
          fontSize: 14,
          lineHeight: 1.43,
          letterSpacing: '0.17px',
          whiteSpace: 'pre-line',
          color: theme.palette.text.primary,
        })}
      >
        {text}
      </Typography>
    </Box>
  );
}
